// The frame every signed-in screen is drawn inside.
//
// THE SERVER MARK IS HERE AND NOT ON EACH SURFACE, and that placement is the
// answer to the roadmap's "wrong-server problem in its most embarrassing form".
// A mark a surface has to remember to include is a mark the next surface forgets,
// and the surface most likely to be added in a hurry is the one somebody writes
// the morning of a show. Drawing it in the frame means a new screen inherits it
// by construction. check.mjs renders each surface through this component and
// asserts the mark's text is in the output of every one of them, which is an
// observation about the screen rather than about which file imports what.
//
// SIGNING OUT IS IN THE FRAME FOR THE SAME REASON, plus one of its own: it has to
// be reachable from a screen that is showing an error. A sign-out button living
// on the dashboard is a sign-out button that disappears exactly when somebody
// wants it, which is when the dashboard is refusing to draw.
//
// THE RAIL IS THE SHARED DESK SHELL, from packages/desk-ui. Same breakpoint,
// same rail on a desk and sheet on a phone, and the same classes the admin desk
// draws with, so the two are one room rather than two rooms with the same
// palette. The wordmark is Nova's rather than a client's, because this surface
// reads every client at once and wearing a client's letters would be the
// wrong-server problem arriving as a logo. It is the plain Nova mark and not
// the Nova XP lockup: on the XP1 token set the shared package's Brand resolves
// to NovaWordmark, so a desk showing the lockup was a desk disagreeing with the
// system it claims to wear. The rail and the stage each
// scroll: a long report cannot carry the account menu off the page, and a long
// menu cannot either.
//
// SIGNING OUT MOVED FROM THE RAIL'S FOOT TO THE ACCOUNT MENU, which is worth
// naming because the header above says sign-out must survive a screen that is
// refusing to draw. It still does: the account button is in the top bar, which
// is part of this frame and outside the stage that holds the failure. What it
// is no longer is a button sitting permanently open at the foot of the rail —
// on a rail long enough to need scrolling, that button was reached by scrolling
// past every page in the show, which is the same problem in slower motion.

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { Toast, useStickyFilters, useToast } from "@nova/desk-ui/kit";
import { ModeIcon, PageFreshnessProvider, RehearsalMark } from "@nova/desk-ui/parts";
import { freshnessSnapshot, subscribeFreshness } from "../api/freshness.js";
import { NovaNMark, NovaWordmark } from "@nova/desk-ui/logos";
import { SkipLink, useDialog } from "@nova/desk-ui/states";
import type { ReportName } from "../api/endpoints.js";
import { CONSOLE_MARK_PARTS, SERVER_SENTENCE } from "../build/thisBuild.js";
import type { BoardPanel, KioskPanel, WheelPanel, RegistrationPanel, RidePanel } from "../routes/route.js";
import type { Session } from "../session/store.js";
import { ThemeToggle } from "../theme/theme.js";
import { ReportNav } from "./ReportNav.js";
import { Stage } from "./Stage.js";
import { SettingsNav, type SettingsPage } from "./SettingsNav.js";
import { showHeading, useShow } from "./Show.js";
import { lifecycle } from "./lifecycle.js";
import { describeRole, initials, roleAndScope } from "./whoami.js";
import { offers, type Reader } from "./roleAccess.js";

export type ChromeNav = {
  readonly activationId: string | null;
  readonly here:
    | ReportName
    | "kiosk"
    | "tablets"
    | "registration"
    | "details"
    | "ride"
    | "people"
    | "unverified"
    | "draw"
    | "wheel"
    | "board"
    | "themes"
    | "activity"
    | "choose"
    | "account"
    | "enrol"
    | "notices"
    | "team"
    | "clients";
  /** Who is reading, as the sign-in said. roleAccess.ts decides what is offered from it. */
  readonly reader: Reader;
  readonly onChoose: () => void;
  readonly onClients: () => void;
  readonly onOpen: (report: ReportName) => void;
  readonly onRegistration: (activationId: string, panel?: RegistrationPanel) => void;
  readonly onKiosk: (activationId: string, panel?: KioskPanel) => void;
  readonly onDetails: (activationId: string) => void;
  readonly onRide: (activationId: string, panel?: RidePanel) => void;
  readonly onPeople: (activationId: string) => void;
  readonly onUnverified: (activationId: string) => void;
  readonly onDraw: (activationId: string) => void;
  readonly onWheel: (activationId: string, panel?: WheelPanel) => void;
  readonly onBoard: (activationId: string, panel?: BoardPanel) => void;
  readonly onActivity: (activationId: string) => void;
  readonly onThemes: (activationId: string) => void;
  /**
   * The company open under Clients, and which of its two pages, or null on
   * the list and everywhere else. The rail hangs Details and Theme under
   * Clients only while one company is open, the way Kiosk's pages hang under
   * Kiosk: a sub-nav drawn from every screen would be a second rail.
   */
  readonly client: { readonly clientId: string; readonly page: "details" | "theme" } | null;
  readonly onClient: (clientId: string, page: "details" | "theme") => void;
  /** The sign-in and security page. Named for what sends people there: a 403. */
  readonly onEnrol: () => void;
  readonly onAccount: () => void;
  readonly onNotices: () => void;
  /** Console Team. Drawn in the rail for an Admin only. */
  readonly onTeam: () => void;
  readonly registrationPanel: RegistrationPanel | null;
  /**
   * Which of the four kiosk pages and two ride pages the address is on.
   *
   * Null while the reader is somewhere else, which is what the rail tests to
   * decide whether to hang the nested list under the parent item at all. The
   * parent carries aria-current only when there is no panel: with one open the
   * current page is a child, and two current pages in one rail is a rail that
   * has told a screen reader two contradictory things.
   */
  readonly kioskPanel: KioskPanel | null;
  /** The wheel page open, or null off the wheel. Its pages hang under Wheel in the rail. */
  readonly wheelPanel: WheelPanel | null;
  /** The board page open, or null off the board. Its pages hang under Board in the rail. */
  readonly boardPanel: BoardPanel | null;
  readonly ridePanel: RidePanel | null;
};

/** A save handler calls this. Plain "Saved" is the in-frame toast; a paragraph stays a notice. */
const ConsoleToastContext = createContext<(message: string) => void>(() => {});

export function useConsoleToast(): (message: string) => void {
  return useContext(ConsoleToastContext);
}

export type ChromeProps = {
  readonly session: Session;
  readonly signingOut: boolean;
  readonly onSignOut: () => void;
  readonly nav: ChromeNav;
  readonly children: ReactNode;
};

function useDeskFreshness(here: ChromeNav["here"], activationId: string | null): {
  readonly generation: number;
  readonly value: {
    readonly checkedAt: number | null;
    readonly refreshing: boolean;
    readonly onRefresh: () => void;
  };
} {
  const [generation, setGeneration] = useState(0);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const sawInflight = useRef(false);

  useEffect(() => {
    sawInflight.current = false;
    setCheckedAt(null);
  }, [here, activationId]);

  useEffect(() => {
    const sync = (): void => {
      const snap = freshnessSnapshot();
      if (snap.inflight > 0) {
        sawInflight.current = true;
        setRefreshing(true);
        return;
      }
      if (sawInflight.current) {
        sawInflight.current = false;
        setCheckedAt(snap.checkedAt);
        setRefreshing(false);
      }
    };
    sync();
    return subscribeFreshness(sync);
  }, []);

  return {
    generation,
    value: {
      checkedAt,
      refreshing,
      onRefresh: () => setGeneration((n) => n + 1),
    },
  };
}

export function Chrome({ session, signingOut, onSignOut, nav, children }: ChromeProps): ReactNode {
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const freshness = useDeskFreshness(nav.here, nav.activationId);
  useStickyFilters([nav.here, nav.activationId, nav.kioskPanel, nav.ridePanel, nav.registrationPanel]);

  useEffect(() => {
    setOpen(false);
  }, [nav.here, nav.activationId, nav.registrationPanel]);

  useEffect(() => {
    document.documentElement.classList.toggle("console-rail-open", open);
    return () => document.documentElement.classList.remove("console-rail-open");
  }, [open]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && open) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const close = (): void => setOpen(false);
  const signOut = (): void => {
    close();
    onSignOut();
  };
  const show = useShow();
  const heading = showHeading(show);
  const showPaused = show.status === "ready" && show.line !== null && lifecycle(show.line) === "Paused";
  // THEMES IS NAMED OVER ITS PAGE RATHER THAN THE SHOW, as the hand-off's bar
  // does (barTitle, IN_THEMES): the library is a client's palettes more than it
  // is this show's page, and the rail already says which show is open.
  const title = nav.here === "themes" ? { text: "Themes", unnamed: false } : (heading ?? outsideAShow(nav.here));
  const settings = offers(nav.reader, "settings")
    ? { account: nav.onAccount, signIn: nav.onEnrol, notices: nav.onNotices }
    : null;

  const railFor = (inSheet: boolean): ReactNode => (
    <Rail
      session={session}
      nav={nav}
      heading={heading}
      close={close}
      inSheet={inSheet}
      onCloseSheet={close}
      signingOut={signingOut}
      onSignOut={signOut}
      onSettings={settings}
    />
  );

  return (
    <ConsoleToastContext.Provider value={toast.say}>
    <div className="frame console-root console-desk">
      <Toast message={toast.message} />
      {CONSOLE_MARK_PARTS === null ? null : (
        // Three pieces, not one string: the label is what somebody catches
        // without reading, the sentence is the explanation, and the server is
        // the detail that only matters once those have landed — so it is drawn
        // quieter. The whole of it still reads as one line, and consoleMark
        // composes the same three parts into the sentence check.mjs looks for on
        // every surface, so the two cannot drift.
        //
        // The shared component owns the close button and the rule behind it: it
        // can be put away for this page view and it is back on the next load,
        // because nothing about the dismissal is written down. RehearsalMark's
        // own header is the long version, including why that is not the same
        // concession the tablet's watermark refuses to make.
        <RehearsalMark
          label={CONSOLE_MARK_PARTS.label}
          sentence={CONSOLE_MARK_PARTS.sentence}
          server={CONSOLE_MARK_PARTS.server}
          className="console-mark"
        />
      )}

      <div className="shell console-shell">
        <SkipLink to="stage" />
        {railFor(false)}

        <main className="main console-main">
          <div className="topbar console-topbar">
            <span className="brand top-brand">
              <NovaWordmark />
            </span>
            {title === null ? null : (
              <p className={title.unnamed ? "top-title console-show-title quiet" : "top-title console-show-title"}>
                {title.text}
                {/* A paused show says so on every one of its pages (C3). */}
                {heading !== null && showPaused ? (
                  <>
                    {" "}
                    <span className="tag tag-warn">Paused</span>
                  </>
                ) : null}
              </p>
            )}
            {/* Theme, profile, and on a phone the menu. The menu is last so
                it is the furthest thing to the right once the rail has folded
                away. The theme sits beside the profile, the same place the
                Black Peak desk puts it, rather than inside the menu. */}
            <div className="top-end">
              <ThemeToggle />
              <span className="top-acct">
                <AccountBar
                  session={session}
                  signingOut={signingOut}
                  onSignOut={signOut}
                  onSettings={settings}
                />
              </span>
              <button
                type="button"
                className="icon-b nav-menu console-menu-btn"
                aria-label="Show navigation"
                aria-expanded={open ? "true" : "false"}
                onClick={() => setOpen(true)}
              >
                <ModeIcon kind="menu" />
              </button>
            </div>
          </div>

          <div className="stage console-page" id="stage">
            {/* Inside the chrome on purpose. See Stage.tsx: a boundary at the
                root catches the same throws and leaves the reader in the same
                empty window, where here the rail outlives the screen and
                leaving a broken page is a click. Keyed on where the reader is,
                so arriving somewhere else clears a caught error rather than
                showing one screen's failure on every screen after it. */}
            <PageFreshnessProvider value={freshness.value}>
              <Stage at={`${nav.here}:${nav.activationId ?? ""}:${String(freshness.generation)}`}>
                {children}
              </Stage>
            </PageFreshnessProvider>
          </div>

          <footer className="foot console-footer">
            <p>{SERVER_SENTENCE}</p>
          </footer>
        </main>
      </div>

      {open ? <SheetNav onClose={close}>{railFor(true)}</SheetNav> : null}
    </div>
    </ConsoleToastContext.Provider>
  );
}

/**
 * The rail, one component drawn twice: the desk's sidebar, and again inside the
 * phone sheet.
 *
 * TWO INSTANCES RATHER THAN ONE ELEMENT MOVED, because the sheet holds Close,
 * Appearance and the account, and the desk rail does not. One component still,
 * so a nav item added to the desk is on the phone. The sheet wraps this rather
 * than reimplementing the list.
 */
/**
 * The orders desk.
 *
 * A link, and not a switch, because that desk signs in somewhere else. A
 * session here does not open it. When the two share a sign-in this becomes a
 * switcher in this same place. Nova staff only: a client viewer has nothing
 * to do with orders, revenue, or Stripe.
 */
const ADMIN_DASHBOARD = "https://admin.novaexhibits.com/admin/dashboard";

function Rail({
  session,
  nav,
  heading,
  close,
  inSheet,
  onCloseSheet,
  signingOut,
  onSignOut,
  onSettings,
}: {
  readonly session: Session;
  readonly nav: ChromeNav;
  readonly heading: { readonly text: string; readonly unnamed: boolean } | null;
  readonly close: () => void;
  readonly inSheet: boolean;
  readonly onCloseSheet: () => void;
  readonly signingOut: boolean;
  readonly onSignOut: () => void;
  readonly onSettings: {
    readonly account: () => void;
    readonly signIn: () => void;
    readonly notices: () => void;
  } | null;
}): ReactNode {
  return (
    <aside className="rail console-rail">
      {inSheet ? (
        <button type="button" className="btn btn-quiet btn-sm sheet-close" onClick={onCloseSheet}>
          Close
        </button>
      ) : null}

      <span className="brand console-rail-brand">
        <NovaWordmark />
      </span>

      <div>
        <div className="rail-sec">Overview</div>
        <nav className="nav" aria-label="Overview">
          <button
            type="button"
            aria-current={nav.here === "choose" ? "page" : undefined}
            onClick={() => {
              nav.onChoose();
              close();
            }}
          >
            Activations
          </button>
          {offers(nav.reader, "clients") ? (
            <button
              type="button"
              // Open, not current, while a company is: the current page is
              // then one of its two below, and two current pages in one rail
              // tell a screen reader two contradictory things.
              className={nav.client === null ? undefined : "open"}
              aria-current={nav.here === "clients" && nav.client === null ? "page" : undefined}
              onClick={() => {
                nav.onClients();
                close();
              }}
            >
              Clients
            </button>
          ) : null}
          {/* The hand-off's CLIENT_PAGES, inside the `.nav` for the reason
              Settings' sub-nav is: furniture.css styles the indent as
              `.nav .sub`. */}
          {offers(nav.reader, "clients") && nav.client !== null ? (
            <nav className="sub" aria-label="This client">
              {(
                [
                  ["Details", "details", true],
                  ["Theme", "theme", offers(nav.reader, "clientTheme")],
                ] as const
              ).filter(([, , offered]) => offered).map(([label, page]) => (
                <button
                  key={page}
                  type="button"
                  aria-current={nav.client?.page === page ? "page" : undefined}
                  onClick={() => {
                    if (nav.client !== null) nav.onClient(nav.client.clientId, page);
                    close();
                  }}
                >
                  {label}
                </button>
              ))}
            </nav>
          ) : null}
        </nav>

        {nav.activationId === null ? null : (
          <ReportNav
            // Every place that is not inside an activation reads as the
            // dashboard here, because this strip is the activation's own and
            // the alternative is a strip with nothing current on it. Spelled
            // out rather than routed through settingsPage(), which returns a
            // value the compiler cannot narrow `nav.here` by: a list the
            // compiler checks is worth more here than a shorter line.
            here={
              nav.here === "choose" ||
              nav.here === "clients" ||
              nav.here === "account" ||
              nav.here === "enrol" ||
              nav.here === "notices" ||
              nav.here === "team"
                ? "operator"
                : nav.here
            }
            onDashboard={(page = "operator") => {
              nav.onOpen(page);
              close();
            }}
            onDetails={() => {
              if (nav.activationId !== null) nav.onDetails(nav.activationId);
              close();
            }}
            onRide={(panel) => {
              if (nav.activationId !== null) nav.onRide(nav.activationId, panel);
              close();
            }}
            ridePanel={nav.ridePanel}
            activationId={nav.activationId}
            onRegistration={(panel) => {
              if (nav.activationId !== null) nav.onRegistration(nav.activationId, panel);
              close();
            }}
            registrationPanel={nav.registrationPanel}
            onKiosk={(panel) => {
              if (nav.activationId !== null) nav.onKiosk(nav.activationId, panel);
              close();
            }}
            kioskPanel={nav.kioskPanel}
            onData={() => {
              nav.onOpen("export");
              close();
            }}
            onPeople={() => {
              if (nav.activationId !== null) nav.onPeople(nav.activationId);
              close();
            }}
            onUnverified={() => {
              if (nav.activationId !== null) nav.onUnverified(nav.activationId);
              close();
            }}
            onDraw={() => {
              if (nav.activationId !== null) nav.onDraw(nav.activationId);
              close();
            }}
            onWheel={(panel) => {
              if (nav.activationId !== null) nav.onWheel(nav.activationId, panel);
              close();
            }}
            wheelPanel={nav.wheelPanel}
            onBoard={(panel) => {
              if (nav.activationId !== null) nav.onBoard(nav.activationId, panel);
              close();
            }}
            boardPanel={nav.boardPanel}
            onActivity={() => {
              if (nav.activationId !== null) nav.onActivity(nav.activationId);
              close();
            }}
            onThemes={() => {
              if (nav.activationId !== null) nav.onThemes(nav.activationId);
              close();
            }}
            reader={nav.reader}
            showTitle={heading === null ? null : heading.text}
            showUnnamed={heading?.unnamed === true}
          />
        )}

        {offers(nav.reader, "settings") || offers(nav.reader, "team") ? (
          <>
            <div className="rail-sec">Account</div>
            <nav className="nav" aria-label="Account">
              {offers(nav.reader, "settings") ? (
                <button
                  type="button"
                  // Current for all three, because Settings is the section and the
                  // sub-nav under it says which of its pages. A rail item that
                  // went quiet the moment somebody opened one of its own children
                  // would leave nothing on a narrow viewport saying which part of
                  // the console they were in.
                  aria-current={settingsPage(nav.here) === null ? undefined : "page"}
                  onClick={() => {
                    nav.onAccount();
                    close();
                  }}
                >
                  Settings
                </button>
              ) : null}
              {/* INSIDE THE `.nav` AND NOT BESIDE IT, which is the whole reason
                  Registration's strip is written the way it is: furniture.css
                  styles the indent as `.nav .sub`, so a sub-nav that is a
                  sibling of the list rather than a child of it draws as three
                  unindented buttons with no rule down their left. Drawn only
                  when one of the three is open, because a sub-nav visible from
                  every screen is a second rail. */}
              {settingsPage(nav.here) === null || !offers(nav.reader, "settings") ? null : (
                <SettingsNav
                  page={settingsPage(nav.here) ?? "account"}
                  onOpen={(page) => {
                    if (page === "account") nav.onAccount();
                    if (page === "signin") nav.onEnrol();
                    if (page === "notices") nav.onNotices();
                    close();
                  }}
                />
              )}
              {/* After Settings, as the October hand-off's rail draws it
                  (console-app.jsx). Team is who can sign in, and the service
                  refuses it to anybody but an Admin. */}
              {offers(nav.reader, "team") ? (
                <>
                  <button
                    type="button"
                    aria-current={nav.here === "team" ? "page" : undefined}
                    onClick={() => {
                      nav.onTeam();
                      close();
                    }}
                  >
                    Team
                  </button>
                  {/* In Account, last, as the October hand-off draws it: the
                      one item that leaves the console, marked by its arrow.
                      Drawn with Team, for the reader the hand-off draws it
                      for: an Admin, whose orders desk it is. */}
                  <a href={ADMIN_DASHBOARD} title="The orders desk. It signs in somewhere else.">
                    Admin dashboard ↗
                  </a>
                </>
              ) : null}
            </nav>
          </>
        ) : null}
      </div>

      <div className="rail-mark" aria-hidden="true">
        <NovaNMark />
      </div>

      {inSheet ? (
        <>
          <div className="rail-sec">Appearance</div>
          <div className="sheet-mode">
            <ThemeToggle />
          </div>
          <div className="sheet-acct">
            <AccountBar
              session={session}
              signingOut={signingOut}
              onSignOut={onSignOut}
              onSettings={onSettings}
            />
          </div>
        </>
      ) : null}
    </aside>
  );
}

/**
 * The rail as an overlay, for a viewport too narrow to hold one beside the
 * stage.
 *
 * useDialog is the shared package's, so the focus trap, the Escape key and the
 * return of focus to whatever opened this behave identically here and on the
 * admin desk. The console used to hand-roll the Escape half of that and had no
 * trap at all, which meant tabbing out of an open menu put focus behind the
 * scrim, on controls the person could not see.
 */
function SheetNav({ onClose, children }: { readonly onClose: () => void; readonly children: ReactNode }): ReactNode {
  const box = useRef<HTMLDivElement>(null);
  useDialog(box, onClose);
  return (
    <div className="sheet sheet-right" ref={box}>
      <button type="button" className="sheet-bd" aria-label="Close menu" onClick={onClose} />
      {children}
    </div>
  );
}

/**
 * Who is signed in, what they may change about that, and the way out.
 *
 * THE EXPIRY IS STILL WRITTEN DOWN, in the menu rather than on the rail. It is
 * the one fact here somebody acts on — a session about to lapse is the reason a
 * long export fails halfway — and dropping it to a tooltip on an avatar, which
 * is what the prototype does, would lose it on every touch device.
 *
 * ============================================ THE HEADER IS THE PACK'S, MINUS A NAME
 *
 * The design draws three lines above the menu: a name in bold, an address under
 * it, and the role and scope in mono. This draws two of the three, and which
 * two is decided by what exists rather than by what fits.
 *
 * There is no name. `identity` has no column for one and nothing asks for one
 * at sign-in; Account.tsx sets that out at length. So the address takes the top
 * line and wears .acct-n, which is the pack's bold, because the top line of
 * this block is what answers "who am I signed in as" and on this service the
 * answer is an address. Leaving .acct-n empty and starting at .acct-e would
 * have drawn the same words a size smaller for no reason a reader could see.
 *
 * A tab with no stored address falls back to the role in the bold line, which
 * is what this menu said before the address was kept at all. It is not a
 * placeholder standing in for an address: it is a different true sentence.
 */
function AccountBar({
  session,
  signingOut,
  onSignOut,
  onSettings,
}: {
  readonly session: Session;
  readonly signingOut: boolean;
  readonly onSignOut: () => void;
  /**
   * The three settings pages, or null where this session is offered none.
   *
   * One object rather than three optional callbacks, so that a menu with one of
   * the three missing is not a shape this component can be handed.
   */
  readonly onSettings: {
    readonly account: () => void;
    readonly signIn: () => void;
    readonly notices: () => void;
  } | null;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const shut = (event: MouseEvent): void => {
      if (!(event.target instanceof Element) || event.target.closest(".acct") === null) setOpen(false);
    };
    document.addEventListener("mousedown", shut);
    return () => document.removeEventListener("mousedown", shut);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="acct" ref={box}>
      <button
        type="button"
        className="acct-b"
        aria-expanded={open}
        aria-haspopup="menu"
        title={describeRole(session)}
        onClick={() => setOpen(!open)}
      >
        {initials(session)}
      </button>
      {open ? (
        <div className="acct-menu" role="menu">
          <div className="acct-who">
            {session.email === null ? (
              <p className="acct-n">{describeRole(session)}</p>
            ) : (
              <>
                <p className="acct-n">{session.email}</p>
                <p className="acct-r">{roleLine(session)}</p>
              </>
            )}
            {/* KEPT, AND KEPT LAST. The pack has no line for it, because its
                fixture never expires. It is the one fact in this block somebody
                acts on: a session about to lapse is the reason a long export
                fails halfway, and it is nowhere else on any screen. */}
            <p className="acct-e quiet">
              Signed in until {new Date(session.expiresAt).toLocaleString()}
            </p>
          </div>
          {onSettings === null ? null : (
            <div className="acct-grp">
              {(
                [
                  ["Account", onSettings.account],
                    ["Sign-in security", onSettings.signIn],
                    ["Notifications", onSettings.notices],
                ] as const
              ).map(([label, go]) => (
                <button
                  key={label}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    go();
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <div className="acct-grp">
            <button type="button" role="menuitem" onClick={onSignOut} disabled={signingOut}>
              {signingOut ? "Signing out…" : "Sign out"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * What the top bar says on a page that is not inside a show.
 *
 * THE BAR WAS EMPTY ON EXACTLY THE PAGES SOMEBODY LANDS ON. The title was the
 * show's name and nothing else, so the activation list, the client list and
 * Settings — the three screens reached without opening anything — drew a bar
 * with an avatar floating in it and no statement of where the reader was. On a
 * narrow viewport, where the rail is a sheet and its current-page mark is behind
 * a menu button, that left nothing at all naming the page.
 *
 * These are the rail's own words for the same three destinations, deliberately:
 * a bar that said "Directory" over a rail item reading "Activations" would be
 * two names for one place. A page inside a show is not here, because the show's
 * name is the better answer and `showHeading` already gives it — the rail says
 * which page of the show, and the bar says which show.
 */
function outsideAShow(here: ChromeNav["here"]): { readonly text: string; readonly unnamed: boolean } | null {
  if (here === "choose") return { text: "Activations", unnamed: false };
  if (here === "clients") return { text: "Clients", unnamed: false };
  if (here === "account") return { text: "Account", unnamed: false };
  if (here === "enrol") return { text: "Sign-in security", unnamed: false };
  if (here === "notices") return { text: "Notifications", unnamed: false };
  if (here === "team") return { text: "Team", unnamed: false };
  return null;
}

/**
 * Which settings page is open, or null for anywhere that is not one.
 *
 * The translation between the routes' names and the sub-nav's, in one function
 * rather than at the three places that want it. `enrol` is the right name for a
 * route a refusal links to and the wrong name for a tab somebody reads, and
 * SettingsNav's header argues that; this is where the two vocabularies meet.
 */
function settingsPage(here: ChromeNav["here"]): SettingsPage | null {
  if (here === "account") return "account";
  if (here === "enrol") return "signin";
  if (here === "notices") return "notices";
  return null;
}

/**
 * The mono line under the address: the role, then what it reaches.
 *
 * Lower-cased after the separator because the pack sets it that way and because
 * the two halves are one sentence rather than two labels. A role this build does
 * not recognise has no scope to state — roleAndScope answers null rather than
 * guessing — and the line is then the role alone.
 */
function roleLine(session: Session): string {
  const { role, scope } = roleAndScope(session);
  return scope === null ? role : `${role} · ${scope.toLowerCase()}`;
}

/**
 * The official Nova XP wordmark, and only that.
 *
 * The N plus typeset "Nova XP" was a stand-in. This surface reads every
 * client at once, so the letters have to be Nova's, not a client's — the
 * wrong-server problem arriving as a logo. XP stays the beige the SVG
 * ships; "nova" follows the desk ink so a dark rail does not swallow it.
 */
// The wordmark is now placed directly by the rail and the top bar, which the
// shared .brand class sizes differently in each. The wrapper this file used to
// keep existed only to carry that class, and a component whose whole body is
// one div and one child is a level of indirection that hides where the mark
// actually sits.

